import React, { Component } from 'react'
import axios from 'axios'
import { Button, Icon } from 'antd'
import ChangePasswordModal from 'modules/shared/components/ChangePasswordModal'
import { PASSWORD_CHANGED_EVENT, formatPasswordExpiryDate } from 'lib/passwordPolicy'

// Header strip: password expires within NOTIFY_DAYS (TECH_SPEC §3.4 / §5), or —
// red, ahead of the expiry one — the account got locked while this session was
// open (user 19/10/2026 · DECISIONS #26; the session ends at its next refresh — #27). Not dismissible. Any error from
// /auth/password-status (e.g. 404 before the BE is deployed) → render nothing.
// The change itself is ChangePasswordModal (shared with the Profile page).
// Status is read on mount, after a password change, whenever the token is
// renewed (~ every 30 minutes) and when the tab comes back into view.

export const PASSWORD_STATUS_PATH = '/auth/password-status'
export {
  CHANGE_PASSWORD_PATH,
  INVALID_OLD_PASSWORD_MESSAGE,
  CHANGE_FAILED_MESSAGE,
  CHANGE_SUCCESS_MESSAGE,
  changePasswordResult,
} from 'modules/shared/components/ChangePasswordModal'

// wording from the user (20/10/2026 · DECISIONS #27): the session now ends at its
// next refresh, so "until you log out" no longer holds
export const ACCOUNT_LOCKED_BANNER_TITLE = 'บัญชีถูกล็อก'
export const ACCOUNT_LOCKED_BANNER_HINT = 'ใช้งานต่อได้จนกว่า session จะหมดอายุ กรุณาติดต่อผู้ดูแลระบบ'

/** The account is locked (only a strict true — anything else is not). */
export function isAccountLockedStatus(status) {
  return Boolean(status) && status.locked === true
}

/** Banner text, or null when there is nothing to warn about. */
export function passwordExpiryText(status) {
  if (!status || status.notify !== true) return null
  const date = formatPasswordExpiryDate(status.expired_at)
  const when = date ? ` (${date})` : ''
  const daysLeft = Number(status.days_left)
  if (status.days_left !== null && status.days_left !== undefined && daysLeft <= 0) {
    return `รหัสผ่านของคุณจะหมดอายุวันนี้${when}`
  }
  return `รหัสผ่านของคุณจะหมดอายุในอีก ${status.days_left} วัน${when}`
}

export class PasswordExpiryBanner extends Component {
  state = {
    status: null,
    modalVisible: false,
  }

  _isMounted = false

  componentDidMount() {
    this._isMounted = true
    window.addEventListener(PASSWORD_CHANGED_EVENT, this.fetchStatus)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.fetchStatus()
  }

  componentDidUpdate(prevProps) {
    // a renewed token (web session refresh) = a cheap moment to look again
    if (this.props.accessToken && this.props.accessToken !== prevProps.accessToken) this.fetchStatus()
  }

  componentWillUnmount() {
    this._isMounted = false
    window.removeEventListener(PASSWORD_CHANGED_EVENT, this.fetchStatus)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.fetchStatus()
  }

  authConfig = () => ({ headers: { Authorization: `Bearer ${this.props.accessToken}` } })

  fetchStatus = () => {
    if (!this.props.accessToken) return Promise.resolve()
    return axios
      .get(`${process.env.REACT_APP_LARAVEL_API_ENDPOINT}${PASSWORD_STATUS_PATH}`, this.authConfig())
      .then(
        (res) => {
          if (this._isMounted) this.setState({ status: res && res.data ? res.data : null })
        },
        () => {
          if (this._isMounted) this.setState({ status: null })
        }
      )
  }

  openModal = () => {
    this.setState({ modalVisible: true })
  }

  closeModal = () => {
    this.setState({ modalVisible: false })
  }

  renderLocked() {
    return (
      <div
        className='password-locked-banner'
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          width: '100%',
          padding: '6px 16px',
          background: '#fff1f0',
          borderBottom: '1px solid #ffa39e',
          color: 'rgba(0, 0, 0, 0.85)',
        }}
      >
        <Icon type='lock' theme='filled' style={{ color: '#f5222d', marginRight: 8 }} />
        <span className='password-locked-banner-title' style={{ fontWeight: 600, marginRight: 12 }}>
          {ACCOUNT_LOCKED_BANNER_TITLE}
        </span>
        <span className='password-locked-banner-hint'>{ACCOUNT_LOCKED_BANNER_HINT}</span>
      </div>
    )
  }

  render() {
    // locked comes first: a locked account cannot change its password anyway
    if (isAccountLockedStatus(this.state.status)) return this.renderLocked()
    const text = passwordExpiryText(this.state.status)
    if (!text) return null
    return (
      <div
        className='password-expiry-banner'
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          width: '100%',
          padding: '6px 16px',
          background: '#fff7e6',
          borderBottom: '1px solid #ffd591',
          color: 'rgba(0, 0, 0, 0.85)',
        }}
      >
        <Icon type='exclamation-circle' theme='filled' style={{ color: '#fa8c16', marginRight: 8 }} />
        <span className='password-expiry-banner-text' style={{ marginRight: 12 }}>
          {text}
        </span>
        {/* a link (user 16/10/2026 · DECISIONS #24 — was a small white button); the
            submit inside the modal stays primary */}
        <Button
          className='password-expiry-banner-link'
          type='link'
          size='small'
          style={{ padding: 0, height: 'auto' }}
          onClick={this.openModal}
        >
          เปลี่ยนรหัสผ่าน
        </Button>
        <ChangePasswordModal
          visible={this.state.modalVisible}
          onClose={this.closeModal}
          accessToken={this.props.accessToken}
        />
      </div>
    )
  }
}

export default PasswordExpiryBanner
